import { useCallback, useEffect, useState } from "react";
import getClient from "../helpers/get-client";

/**
 * Uso de la semana en curso del médico logueado (item Planner #1209).
 *
 * Alimenta la píldora del navbar. Todo el cálculo vive en el backend
 * (`GET /usage/me/semana/`): el porcentaje, el presupuesto y los umbrales del
 * semáforo. Acá NO se calcula nada — recalibrar el presupuesto no puede exigir
 * un redeploy del bundle, misma razón que en `useContextLimits`.
 *
 * Fail-silent estricto: si el endpoint falla, tarda o devuelve algo raro,
 * `usage` queda en null y la píldora simplemente no se dibuja. Un contador de
 * uso jamás puede mostrarle un error a alguien que está pasando visita.
 */

// `percent`/`nivel` en null NO es un cuerpo inválido: es un plan sin tope
// (interno, cuenta técnica, Max+; `gratis` sí tiene tope). Ahí hay consumo pero no
// hay porcentaje que calcular, así que la barra muestra el número y no finge
// una proporción. Rechazar esos cuerpos escondería la barra justo a las cuentas
// del equipo.
const esValido = (d) => {
  if (!d || typeof d !== "object") return false;
  if (typeof d.documentos !== "number") return false;
  const sinTope = d.percent === null || d.percent === undefined;
  if (sinTope) return d.nivel === null || d.nivel === undefined;
  return typeof d.percent === "number" && ["ok", "warn", "over"].includes(d.nivel);
};

// Último valor conocido en esta pestaña. Sin esto, cada apertura del menú monta
// el hook de cero: el menú abre sin la barra, y cuando llega la respuesta la
// barra entra y empuja los chips de cuenta 70 px hacia abajo — un click
// apuntado a "Comunidad" (ex "Colegas") cae sobre la barra. Con el último valor la barra ya está
// dibujada al abrir y solo se actualiza si cambió.
//
// NO es una cache que reemplace el refresco: si el dato tiene más de VENTANA_MS
// se vuelve a pedir igual, y el evento de consumo y el volver a la pestaña
// fuerzan siempre. Y si el endpoint falla, se BORRA (R11: ante error no se
// muestra nada, tampoco un valor viejo).
let ultimo = null;
let ultimoAt = 0;
const VENTANA_MS = 30000;

// El valor vive en el MÓDULO y las instancias montadas se suscriben. Antes cada
// instancia se guardaba su propio `vivo` y solo ella aplicaba su respuesta: si
// la instancia que lanzó el request se desmontaba (abrir/cerrar el menú) y el
// request fallaba, el error borraba `ultimo` pero NADIE se lo decía a la
// instancia que sí estaba en pantalla — que además no re-pedía por la ventana
// de 30 s. Resultado: la píldora se quedaba con el número viejo y el backend
// caído, justo lo que R11 prohíbe. Con suscriptores, toda respuesta (éxito o
// error) llega a todas las instancias vivas, la haya pedido quien la haya
// pedido.
const suscriptores = new Set();

// Secuencia: una respuesta vieja que llega tarde no puede pisar a una más
// nueva (el caso clásico: el éxito lento sobrescribiendo el error reciente).
let seq = 0;

const publicar = (dato) => {
  ultimo = dato;
  ultimoAt = dato ? Date.now() : 0;
  suscriptores.forEach((notificar) => notificar(dato));
};

export default function useWeeklyUsage(enabled = true) {
  const [usage, setUsage] = useState(ultimo);

  const fetchUsage = useCallback(
    (forzar = false) => {
      if (!enabled) return undefined;
      if (!forzar && ultimo && Date.now() - ultimoAt < VENTANA_MS) return undefined;
      const mio = ++seq;
      getClient()
        .get("/api/medical_analysis/usage/me/semana/")
        .then((res) => {
          if (mio !== seq) return; // llegó tarde: ya hay una petición más nueva
          publicar(esValido(res?.data) ? res.data : null);
        })
        .catch(() => {
          // Silencio a propósito (ver docstring). Se borra el último valor: una
          // barra que sigue mostrando el número de hace un rato mientras el
          // backend no responde es peor que no mostrar nada.
          if (mio !== seq) return;
          publicar(null);
        });
      return undefined;
    },
    [enabled]
  );

  // Suscripción al valor del módulo: así una respuesta que pidió OTRA instancia
  // (o una que ya se desmontó) igual actualiza esta.
  useEffect(() => {
    suscriptores.add(setUsage);
    setUsage(ultimo);
    return () => suscriptores.delete(setUsage);
  }, []);

  useEffect(() => fetchUsage(), [fetchUsage]);

  // Al volver a la pestaña se refresca: el médico genera documentos y vuelve.
  // Sin polling — un contador informativo no justifica un timer corriendo todo
  // el turno.
  useEffect(() => {
    if (!enabled) return undefined;
    const alVolver = () => {
      if (document.visibilityState === "visible") fetchUsage(true);
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [enabled, fetchUsage]);

  // Y al terminar de generar algo (evento de `get-client`), para que el número
  // acompañe el trabajo en vez de quedarse en el que había al abrir la app.
  // Se agrupan las ráfagas: "Generar turno" hace una llamada POR CAMA y no
  // vamos a pedir el uso 20 veces seguidas.
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const alConsumir = () => {
      clearTimeout(timer);
      timer = setTimeout(() => fetchUsage(true), 2500);
    };
    window.addEventListener("smartdx:usage-changed", alConsumir);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("smartdx:usage-changed", alConsumir);
    };
  }, [enabled, fetchUsage]);

  return usage;
}
